<template>
  <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 py-4">
    <div class="flex justify-between items-center px-4">
      <div class="flex items-center gap-2">
        <Button color="glass" size="xs" :disabled="isEditing" @click="handleImportMacro">
          <Icon icon="Function-Wireframe-FileImport" class="mt-px" />
          {{ $t('keys.macro.buttons.import') }}
        </Button>
        <Button
          color="glass"
          size="xs"
          :disabled="list.length === 0 || (isEditing && editSelection.size === 0)"
          @click="handleExportClick"
        >
          <Icon icon="Function-Wireframe-FileExport" class="mt-px" />
          {{ $t('keys.macro.buttons.export') }}
        </Button>
        <Tooltip v-if="list.length > 12" :content="$t('keys.macro.buttons.locate')">
          <Button
            color="glass"
            size="icon-xs"
            :disabled="selectedIndex < 0 || isEditing"
            @click="scrollToSelected"
          >
            <Icon icon="Technology-Wireframe-Tracking" class="mt-px" />
          </Button>
        </Tooltip>
        <Tooltip>
          <Button color="normal" size="icon-xs" class="cursor-default">
            <Icon icon="Function-Wireframe-Caution" class="mt-px text-orange-active" />
          </Button>
          <template #content>
            <p
              class="text-s-medium text-white-black w-56 bg-floating-tooltip-container rounded-m p-1"
            >
              {{ $t('keys.macro.buttons.tip') }}
            </p>
          </template>
        </Tooltip>
      </div>

      <template v-if="isEditing">
        <Button color="glass" size="xs" @click="handleCancelExport">
          {{ $t('keys.macro.buttons.cancelSelection') }}
        </Button>
      </template>
      <template v-else>
        <Button color="glass" size="xs" :disabled="disabled" @click="handleCreate">
          <Icon icon="Function-Wireframe-Add11" class="mt-px" />
          {{ $t('keys.macro.buttons.create') }}
        </Button>
      </template>
    </div>

    <ScrollArea v-if="list.length" ref="scrollAreaRef" class="flex-1 min-h-0 mt-4 pl-4 pr-2 mr-2">
      <TransitionGroup name="macro-list" tag="div" class="flex flex-col gap-2 relative">
        <div
          v-for="(item, idx) in list"
          :key="item.index"
          class="macro-list-item w-full h-9 text-s-medium text-black flex justify-between items-center gap-1.5 rounded-full px-3 shrink-0 transition-colors"
          :class="
            isEditing
              ? 'bg-white'
              : idx === selectedIndex
                ? 'bg-white text-brand-blue2!'
                : 'glass-frosted'
          "
          @click="isEditing ? toggleSelection(item.index) : emit('select', idx)"
        >
          <input
            v-if="editingIndex === idx"
            ref="editingInputRef"
            v-model.trim="editingName"
            class="flex-1 min-w-0 bg-transparent outline-none"
            maxlength="50"
            @blur="handleRenameCommit(item, idx)"
            @keyup.enter="($event.target as HTMLInputElement)?.blur()"
            @keyup.esc="cancelRename"
          />

          <span
            v-else
            class="flex-1 min-w-0 truncate select-none"
            @dblclick.stop.prevent="handleRenameStart(item, idx)"
          >
            {{ item.name }}
          </span>

          <template v-if="!isEditing">
            <button
              type="button"
              class="inline-flex items-center justify-center cursor-pointer w-5 h-full text-neutral-dark-default transition-colors hover:text-blue-dark-hover active:text-brand-blue1"
              :disabled="disabled"
              @click.stop="handleDelete(item, idx)"
            >
              <Icon icon="Function-Wireframe-Delete" class="size-5" />
            </button>

            <!-- 绑定按钮：仅在宏有已保存内容时显示，支持独立高亮 -->
            <button
              type="button"
              class="inline-flex items-center justify-center cursor-pointer w-5 h-full transition-colors"
              :class="
                highlightedBindIndex === idx
                  ? 'text-brand-blue1'
                  : 'text-neutral-dark-default hover:text-blue-dark-hover active:text-brand-blue1'
              "
              :disabled="disabled"
              @click.stop="handleBindClick(item, idx)"
            >
              <Icon icon="Technology-Wireframe-BindKey" class="size-5" />
            </button>
          </template>

          <template v-else>
            <Icon
              :icon="
                editSelection.has(item.index) ? 'Switch-Fill-Check' : 'Switch-Wireframe-Unselected'
              "
              class="size-5 cursor-pointer shrink-0"
              :class="editSelection.has(item.index) ? 'text-brand-blue1' : ''"
              @click.stop="toggleSelection(item.index)"
            />
          </template>
        </div>
      </TransitionGroup>
    </ScrollArea>

    <div v-else class="flex flex-col justify-center items-center mt-2 gap-2">
      <img class="size-42.5" :src="emptyTip" />
      <p class="text-text-glass-default text-s-medium">
        {{ $t('keys.macro.addTip') }}
      </p>
    </div>

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('keys.macro.deleteDialog.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('keys.macro.deleteDialog.content') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="isDeleteDialogOpen = false">
            {{ $t('keys.macro.buttons.cancel') }}
          </Button>
          <Button color="text-b-w" @click="handleConfirmDelete">
            {{ $t('keys.macro.buttons.delete') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <input
      ref="fileInputRef"
      type="file"
      accept=".json"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>

<script setup lang="ts">
  import { inject, nextTick, ref, watch } from 'vue'
  import JSZip from 'jszip'
  import { Button, Icon, toast, Dialog, ScrollArea, Tooltip } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import type { Macro } from '@rapoo/device-sdk'
  import { useMacroRepository as useDefaultMacroRepository } from '../composables/useMacroRepository'
  import { MacroRepositorySymbol, type MacroListItem, type UseMacroRepository } from '../types'
  import { useMacro } from '../composables/useMacro'
  import {
    getMacroActionEncodedByteLength,
    takeActionsWithinMacroByteLength,
  } from '../utils/macroByteLength'

  import emptyTip from '@/assets/images/devices/keys/empty.webp'
  import { defaultReportError } from '@/composables/useErrorLogger'

  const { t } = useLocaleStore()

  const props = withDefaults(
    defineProps<{
      list?: MacroListItem[]
      selectedIndex?: number
      highlightedBindIndex?: number | null
      disabled?: boolean
      /** 最大宏数量，默认 128（键盘） */
      maxMacroCount?: number
      /** 单个宏内容最大字节数，默认 512（键盘） */
      maxMacroByteLength?: number
    }>(),
    {
      list: () => [],
      selectedIndex: -1,
      highlightedBindIndex: null,
      disabled: false,
      maxMacroCount: 128,
      maxMacroByteLength: 512,
    },
  )

  const scrollAreaRef = ref<{ $el?: HTMLElement } | null>(null)

  const getListViewport = () => {
    const root = scrollAreaRef.value?.$el
    if (!(root instanceof HTMLElement)) return null
    return root.querySelector<HTMLElement>('[data-reka-scroll-area-viewport]')
  }

  /** 新增宏超出可视区域时滚到底部 */
  const scrollListToBottom = async () => {
    // 等列表 DOM / v-if 的 ScrollArea 挂载完成
    await nextTick()
    await nextTick()
    const viewport = getListViewport()
    if (!viewport) return
    if (viewport.scrollHeight <= viewport.clientHeight) return

    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: 'smooth',
    })
  }

  /** 滚动定位到当前选中的宏（仅滚动列表视口，并修正页面 scale 带来的坐标偏差） */
  const scrollToSelected = async () => {
    if (props.selectedIndex < 0) return

    await nextTick()
    const viewport = getListViewport()
    if (!viewport) return

    const items = viewport.querySelectorAll<HTMLElement>('.macro-list-item')
    const target = items[props.selectedIndex]
    if (!target) return

    // 列表项等高，用布局尺寸推算偏移，避免 getBoundingClientRect 受 transform:scale 影响
    const itemHeight = target.offsetHeight
    const stride = items.length > 1 ? items[1]!.offsetTop - items[0]!.offsetTop : itemHeight
    const offsetTop = props.selectedIndex * stride
    const nextTop = offsetTop - (viewport.clientHeight - itemHeight) / 2
    const maxTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight)

    viewport.scrollTo({
      top: Math.min(Math.max(0, nextTop), maxTop),
      behavior: 'smooth',
    })
  }

  watch(
    () => props.list,
    (list, oldList) => {
      // 只跟数组替换：新建/导入是 [...list, item]。
      // 重新进入时 loadMacroList 会先清空再 push，引用不变，不能滚。
      if (!oldList || list === oldList || list.length <= oldList.length) return
      scrollListToBottom()
    },
  )

  // 允许外部（如鼠标侧）通过 provide 注入不同的宏仓储实现，未提供则使用键盘默认实现。
  const useMacroRepository = inject<UseMacroRepository>(
    MacroRepositorySymbol,
    () => useDefaultMacroRepository,
    true,
  )

  const { readMacroByIndex, saveMacroByIndex, macroList } = useMacroRepository()
  const { macroToJson, jsonToMacro } = useMacro()

  const emit = defineEmits<{
    (e: 'create'): void
    (e: 'select', index: number): void
    (e: 'edit', payload: { item: MacroListItem; name: string; listIndex: number }): void
    (e: 'delete', payload: { item: MacroListItem; listIndex: number }): void
    (e: 'bind-key', payload: { item: MacroListItem; index: number }): void
  }>()

  const editingIndex = ref<number | null>(null)
  const editingName = ref('')
  const editingInputRef = ref<HTMLInputElement | HTMLInputElement[] | null>(null)

  // 删除宏相关
  const isDeleteDialogOpen = ref(false)
  const deletingItem = ref<MacroListItem | null>(null)
  const deletingIndex = ref(-1)

  // 导出状态
  const isEditing = ref(false)
  const editSelection = ref(new Set<number>())

  const toggleSelection = (index: number) => {
    const next = new Set(editSelection.value)
    if (next.has(index)) {
      next.delete(index)
    } else {
      next.add(index)
    }
    editSelection.value = next
  }

  // 宏文件导出
  const handleExportClick = async () => {
    if (!isEditing.value) {
      // 进入导出选择模式
      isEditing.value = true
      editSelection.value = new Set()
      return
    }

    // 已在导出模式，执行导出
    if (editSelection.value.size === 0) return

    const macros: Macro[] = []
    for (const idx of editSelection.value) {
      macros.push(await readMacroByIndex(idx))
    }

    // 单选直接保存 json；多选打包为 zip 一次保存
    const saved =
      macros.length === 1 ? await onSaveMacroFile(macros[0]!) : await onSaveMacroZip(macros)

    // 用户取消系统保存弹窗时不提示成功、不退出选择模式
    if (!saved) return

    toast.success(t('toast.keys.exportSuccess'))

    isEditing.value = false
    editSelection.value = new Set()
  }

  const handleCancelExport = () => {
    isEditing.value = false
    editSelection.value = new Set()
  }

  type SavePickerType = {
    description: string
    accept: Record<string, string[]>
  }

  /**
   * 支持两种导出方法
   * 1.showSaveFilePicker 针对现代浏览器优先使用，不触发浏览器下载行为
   * 2.blob 保存
   * @returns 是否实际完成保存（用户取消系统弹窗时为 false）
   */
  const saveBlobAsFile = async (
    blob: Blob,
    fileName: string,
    pickerTypes: SavePickerType[],
  ): Promise<boolean> => {
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: fileName,
          types: pickerTypes,
        })

        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        return true
      } catch (e) {
        // 用户取消保存对话框
        if (e instanceof DOMException && e.name === 'AbortError') return false
        console.error(e)
        defaultReportError(e)
      }
    }

    const url = URL.createObjectURL(blob)
    const alink = document.createElement('a')
    alink.href = url
    alink.download = fileName
    alink.click()
    URL.revokeObjectURL(url)
    return true
  }

  const onSaveMacroFile = async (data: Macro) => {
    const json = JSON.stringify(macroToJson(data), null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    return saveBlobAsFile(blob, `${data.name}.json`, [
      {
        description: 'JSON',
        accept: { 'application/json': ['.json'] },
      },
    ])
  }

  /** 同名宏在 zip 内去重：name.json → name (1).json */
  const getUniqueZipEntryName = (baseName: string, used: Set<string>) => {
    const safeBase = (baseName || 'Macro').replace(/[\\/:*?"<>|]/g, '_')
    let fileName = `${safeBase}.json`
    if (!used.has(fileName)) {
      used.add(fileName)
      return fileName
    }

    let suffix = 1
    while (used.has(`${safeBase} (${suffix}).json`)) suffix += 1
    fileName = `${safeBase} (${suffix}).json`
    used.add(fileName)
    return fileName
  }

  const onSaveMacroZip = async (macros: Macro[]) => {
    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const macro of macros) {
      const entryName = getUniqueZipEntryName(macro.name, usedNames)
      zip.file(entryName, JSON.stringify(macroToJson(macro), null, 2))
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    return saveBlobAsFile(blob, 'macros.zip', [
      {
        description: 'ZIP',
        accept: { 'application/zip': ['.zip'] },
      },
    ])
  }

  const fileInputRef = ref<HTMLInputElement>()

  // 宏文件导入
  const handleImportMacro = () => {
    fileInputRef.value?.click()
  }

  /**
   * 导入文件校验
   * 1.校验是否有文件
   * 2.检验文件格式
   * 3.校验文件内容字段
   * 4.校验ID字段是否重复
   */
  const handleFileChange = async (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) return

    const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
    if (ext !== 'json') {
      toast.error(t('toast.keys.macroJsonOnly'))
      target.value = ''
      return
    }

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // 一级字段校验
      if (!data.Macro || !data.Platform) {
        toast.error(t('toast.keys.macroMissingTopLevel'))
        target.value = ''
        return
      }

      // 二级字段校验
      const macro = data.Macro
      const requiredFields = ['CycleNumber', 'ID', 'IgnoreDelay', 'InfoList', 'Name', 'PlayType']
      const missingFields = requiredFields.filter((field) => !(field in macro))
      if (missingFields.length > 0) {
        toast.error(t('toast.keys.macroMissingFields', { fields: missingFields.join(', ') }))
        target.value = ''
        return
      }

      // 校验列表长度是否已达上限
      if (macroList.value.length >= props.maxMacroCount) {
        toast.warning(t('toast.keys.macroMax', { count: props.maxMacroCount }))
        target.value = ''
        return
      }

      // ID 冲突时改用最小空闲 slot，不中断导入
      const usedIds = new Set(macroList.value.map((item) => Number(item.index)))
      let importId = Number(macro.ID)
      if (!Number.isInteger(importId) || importId < 0 || usedIds.has(importId)) {
        const freeId = Array.from({ length: props.maxMacroCount }, (_, id) => id).find(
          (id) => !usedIds.has(id),
        )
        if (freeId === undefined) {
          toast.warning(t('toast.keys.macroMax', { count: props.maxMacroCount }))
          target.value = ''
          return
        }
        importId = freeId
      }
      macro.ID = importId

      // 转换并保存。
      // IgnoreDelay 在固件侧会把每个动作（含原本无延迟的坐标前动作）都写成 2ms 哨兵，
      // 回显时会被展开成额外延迟卡片；导入时保留文件里的真实延迟结构。
      const sdkMacro = jsonToMacro(data)
      sdkMacro.ignoreDelay = false
      sdkMacro.index = importId

      // 超过协议上限，或落进写满槽位的短尾包时，从放不下的动作起整段去掉。刚好写得下的保留。
      const fitted = takeActionsWithinMacroByteLength(
        sdkMacro.package ?? [],
        props.maxMacroByteLength,
        (action) => getMacroActionEncodedByteLength(action.key),
      )
      if (fitted.truncated && fitted.actions.length === 0) {
        toast.error(t('toast.keys.macroRecordingLimit2'))
        target.value = ''
        return
      }
      sdkMacro.package = fitted.actions
      if (fitted.truncated) {
        toast.warning(t('toast.keys.macroRecordingLimit2'))
      }

      await saveMacroByIndex(sdkMacro, importId)

      // 新增到列表（无 Name 时按列表序号兜底，与新建宏一致）
      const newItem: MacroListItem = {
        name: macro.Name || `Macro${macroList.value.length + 1}`,
        index: importId,
        hasContent: Array.isArray(macro.InfoList) && macro.InfoList.length > 0,
      }
      macroList.value = [...macroList.value, newItem]

      // 如果是唯一一项则选中
      if (macroList.value.length === 1) {
        emit('select', 0)
      }

      toast.success(t('toast.keys.macroImportSuccess'))
    } catch (err) {
      console.error(err)
      toast.error(t('toast.keys.macroParseFailed'))
      defaultReportError(`${t('toast.keys.macroParseFailed')}: ${JSON.stringify(err)}`)
    }

    target.value = ''
  }

  const getByteLength = (value: string) => {
    let bytes = 0
    for (const char of value) {
      const code = char.charCodeAt(0)
      if (code <= 0x7f) bytes += 1
      else if (code <= 0x7ff) bytes += 2
      else bytes += 3
    }
    return bytes
  }

  const trimByByteLength = (value: string, maxBytes: number) => {
    let bytes = 0
    let result = ''

    for (const char of value) {
      const code = char.charCodeAt(0)
      const next = code <= 0x7f ? 1 : code <= 0x7ff ? 2 : 3
      if (bytes + next > maxBytes) break

      bytes += next
      result += char
    }

    return result
  }

  const handleCreate = () => {
    if (props.disabled) return

    if (props.list.length >= props.maxMacroCount) {
      toast.warning(t('toast.keys.macroMax', { count: props.maxMacroCount }))
      return
    }

    emit('create')
  }

  // 进入宏文件名称编辑状态
  const handleRenameStart = async (item: MacroListItem, listIndex: number) => {
    if (isEditing.value) return

    editingIndex.value = listIndex
    editingName.value = item.name

    await nextTick()

    const input = Array.isArray(editingInputRef.value)
      ? editingInputRef.value[0]
      : editingInputRef.value

    if (!input) return

    input.focus({ preventScroll: true })
    input.setSelectionRange(input.value.length, input.value.length)
  }

  const cancelRename = () => {
    editingIndex.value = null
    editingName.value = ''
  }

  // 宏文件名称重命名提交
  const handleRenameCommit = async (item: MacroListItem, listIndex: number) => {
    const raw = editingName.value.trim()
    const normalized = trimByByteLength(raw, 16)

    if (!normalized) {
      toast.warning(t('toast.keys.macroRenameEmpty'))
      cancelRename()
      return
    }

    if (getByteLength(raw) > 16) {
      toast.info(t('toast.keys.macroRenameTruncated'))
    }

    emit('edit', {
      item,
      name: normalized,
      listIndex,
    })

    cancelRename()
  }

  /**
   * 确认删除宏文件
   */
  const handleConfirmDelete = () => {
    if (!deletingItem.value) return

    emit('delete', {
      item: deletingItem.value,
      listIndex: deletingIndex.value,
    })

    isDeleteDialogOpen.value = false
    deletingItem.value = null
    deletingIndex.value = -1
  }

  // 删除文件弹窗
  const handleDelete = (item: MacroListItem, listIndex: number) => {
    isDeleteDialogOpen.value = true
    deletingIndex.value = listIndex
    deletingItem.value = item
  }

  // 点击icon进行绑定
  const handleBindClick = async (item: MacroListItem, idx: number) => {
    if (props.disabled) return

    const macro = await readMacroByIndex(item.index)
    if (macro.package?.length === 0) return toast.error(t('toast.keys.macroEmptyUnsupported'))

    emit('bind-key', { item, index: idx })
  }
</script>

<style lang="scss" scoped>
  .macro-list-enter-active,
  .macro-list-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .macro-list-enter-from,
  .macro-list-leave-to {
    opacity: 0;
    transform: scale(0.98);
  }

  .macro-list-move {
    transition: transform 0.2s ease;
  }

  .macro-list-leave-active {
    position: absolute;
    width: 100%;
  }
</style>
